import { formatApp, formatAppDateTime, appNow, toApp } from '../../services/timezone';
import { getDefaults } from '../../services/detailDefaults';
/* Relative day label for a group header: Today / 1 Day ago / N Days ago.
 *
 * Measured in the TENANT's day, not the browser's. Mixing the two is how a note
 * written at 11pm IST ends up filed under "1 Day ago" for a colleague reading
 * it from London — the row already prints its time in the tenant zone, so the
 * header has to agree with it. */
export function relativeDay(date) {
  const today = appNow().startOf('day');
  const diffDays = today.diff(toApp(date).startOf('day'), 'day');

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 Day ago';
  return `${diffDays} Days ago`;
}

/* Group timeline rows by calendar day, newest day first.
 *
 * The day boundary is the TENANT's midnight. Grouping by the browser's while
 * each row prints the tenant's time put rows under a day their own timestamp
 * contradicted — the mismatch was visible on screen. */
export function groupByDay(rows = [], getTime) {
  const groups = new Map();

  rows.forEach((row) => {
    const value = getTime(row);
    const local = toApp(value);
    if (!local.isValid()) return;
    const key = local.format('YYYY-MM-DD');
    if (!groups.has(key)) {
      // `date` is kept as a real Date for the header label and the sort; it is
      // the same instant, only the DAY it is filed under is tenant-scoped.
      groups.set(key, { date: new Date(local.valueOf()), items: [] });
    }
    groups.get(key).items.push(row);
  });

  return [...groups.values()].sort((a, b) => b.date - a.date);
}

/* dateOnlyFormat — the DATE half of the tenant's configured date-time format.
 *
 * Derived rather than hardcoded so a day header and a full timestamp cannot
 * disagree about how a date is written. With the default
 * "MMM DD, YYYY | hh:mm A" this yields "MMM DD, YYYY", so a group header reads
 * "Aug 06, 2026" and the row beneath it "Aug 06, 2026 | 10:07 AM IST" — the
 * same date, spelled the same way.
 *
 * The separator is the pipe the format already uses to divide the two halves.
 * A format with no pipe is used whole: it is presumably date-only already, and
 * inventing a split would be a guess. */
function dateOnlyFormat(defaults) {
  const full = defaults?.dateTimeFormat || 'MMM DD, YYYY | hh:mm A';
  const [datePart] = full.split('|');
  return (datePart ?? full).trim() || full;
}

/* Full date-only label, tenant zone. Used for the tooltip and anywhere a day
 * is named on its own. */
export function formatDate(value) {
  const defaults = getDefaults();
  return formatApp(value, dateOnlyFormat(defaults), defaults);
}

/* The day a group of rows is filed under. Same spelling as the date half of
 * every timestamp below it. */
export function formatShortDate(value) {
  return formatDate(value);
}

/* The timestamp on an activity or note row.
 *
 * ONE value in the tenant's standard format — "Aug 06, 2026 | 10:07 AM IST" —
 * not a time and a date rendered separately, in the opposite order, with a
 * different date spelling from every other timestamp in the app. That is what
 * this was: "10:07 AM EST/EDT" and "Aug 6, 2026" as two spans, reading
 * time-then-date, while the record header two inches above read
 * "Aug 06, 2026 | 07:03 PM".
 *
 * It goes through formatAppDateTime like every other timestamp, so it follows
 * the admin's date format, the admin's time zone, and the admin's choice about
 * whether the zone label is shown. */
export function formatTimestamp(value) {
  return formatAppDateTime(value, getDefaults());
}
